import React, { createContext, useState, useContext, useEffect } from 'react';
import { auth } from '../firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userRoles, setUserRoles] = useState(null);

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          // Force refresh token to get latest custom claims
          const idTokenResult = await user.getIdTokenResult(true);
          // Support both a simple `admin: true` legacy claim and new `roles` claim
          const rolesClaim = idTokenResult.claims.roles || (idTokenResult.claims.admin ? 'Admin' : null);

          if (!rolesClaim) {
            // If no roles assigned, sign out and block access
            await handleLogout();
            alert('You are not authorized to access this application.');
            setCurrentUser(null);
            setUserRoles(null);
          } else {
            setCurrentUser(user);
            setUserRoles(rolesClaim);
          }
        } catch (error) {
          console.error('Error checking user roles:', error);
          await handleLogout();
          setCurrentUser(null);
          setUserRoles(null);
        }
      } else {
        setCurrentUser(null);
        setUserRoles(null);
      }
      setLoading(false);
    });

    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // allowedRoles: array of role names. If empty or not provided, any authenticated user is allowed.
  const isAuthorized = (allowedRoles = []) => {
    if (!currentUser) return false;
    if (!allowedRoles || allowedRoles.length === 0) return true;
    
    // Normalize userRoles to always be an array for consistent checking
    const rolesArray = Array.isArray(userRoles) ? userRoles : [userRoles];
    return allowedRoles.some(role => rolesArray.includes(role));
  };

  const value = {
    currentUser,
    loading,
    userRoles,
    isAuthorized,
    handleLogout
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
